import * as React from "react";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { useMutation, useQuery } from "convex/react";
import {
  Archive,
  ChevronRight,
  ExternalLink,
  Home,
  Loader2,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { api } from "$convex/convexApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/format";

type DocumentStatus = "draft" | "published" | "archived";
type SourceType = "manual" | "import" | "file_upload" | "external";

interface DocumentEditorProps {
  authUserId: string;
  documentId: string;
  onClose: () => void;
}

const LOCAL_FILESYSTEM_PATH_PATTERN = /^(\/|~\/|[a-zA-Z]:[\\/]|\\\\|file:\/\/)/;
const EMBEDDED_LOCAL_SOURCE_LINE_PATTERN =
  /^(Source(?: Folder)?|Path):\s*`(\/Users\/[^`]+|~\/[^`]+|[a-zA-Z]:[\\/][^`]+)`/gm;

function isLocalFilesystemPath(value: string | undefined | null): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return (
    LOCAL_FILESYSTEM_PATH_PATTERN.test(trimmed) ||
    trimmed.includes("/Users/") ||
    trimmed.includes("\\Users\\")
  );
}

function filenameFromPath(input: string): string {
  const segments = input.split(/[\\/]/).filter(Boolean);
  return segments[segments.length - 1] ?? input;
}

function sanitizeEmbeddedSourceMarkdown(markdown: string): string {
  if (!markdown) return "";
  return markdown.replace(EMBEDDED_LOCAL_SOURCE_LINE_PATTERN, (_full, label, sourcePath) => {
    return `${label}: \`${filenameFromPath(sourcePath)}\``;
  });
}

function sanitizeSourcePathDisplay(sourcePath: string | undefined): string {
  const normalized = (sourcePath ?? "").trim();
  if (!normalized) return "";
  return isLocalFilesystemPath(normalized) ? "" : normalized;
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function normalizeSourceReference(value: string): string {
  const normalized = value.trim();
  if (!normalized) return "";

  const openByIdMatch = normalized.match(
    /^https?:\/\/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/i,
  );
  if (openByIdMatch) {
    return `https://drive.google.com/file/d/${openByIdMatch[1]}/view`;
  }

  const ucByIdMatch = normalized.match(
    /^https?:\/\/drive\.google\.com\/uc\?id=([a-zA-Z0-9_-]+)/i,
  );
  if (ucByIdMatch) {
    return `https://drive.google.com/file/d/${ucByIdMatch[1]}/view`;
  }

  return normalized;
}

function normalizeFolderPath(value: string | undefined | null): string {
  if (!value) return "";
  return value
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join("/");
}

function parseTags(tagsText: string): string[] | undefined {
  const unique = new Map<string, string>();
  const tokens = tagsText
    .split(/[,\n]/g)
    .map((tag) => tag.trim())
    .filter(Boolean);

  for (const tag of tokens) {
    const key = tag.toLowerCase();
    if (!unique.has(key)) unique.set(key, tag);
  }

  const tags = Array.from(unique.values());
  return tags.length > 0 ? tags : undefined;
}

function statusBadgeClass(status: DocumentStatus): string {
  if (status === "published") return "border-emerald-300 text-emerald-700 bg-emerald-50";
  if (status === "archived") return "border-zinc-300 text-zinc-700 bg-zinc-50";
  return "border-amber-300 text-amber-700 bg-amber-50";
}

const markdownPreviewOptions: NonNullable<React.ComponentProps<typeof MDEditor>["previewOptions"]> = {
  components: {
    a: ({ node: _node, ...props }: React.ComponentPropsWithoutRef<"a"> & { node?: unknown }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
    ),
  },
};

export function DocumentEditor({ authUserId, documentId, onClose }: DocumentEditorProps) {
  const { resolvedTheme } = useTheme();
  const [title, setTitle] = React.useState("");
  const [contentMarkdown, setContentMarkdown] = React.useState("");
  const [status, setStatus] = React.useState<DocumentStatus>("draft");
  const [folderPath, setFolderPath] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [lastSaved, setLastSaved] = React.useState<number | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const document = useQuery(
    api.knowledgeBase.getDocumentById,
    authUserId && documentId
      ? {
          authUserId,
          documentId: documentId as any,
        }
      : "skip",
  );

  const upsertDocument = useMutation(api.knowledgeBase.upsertDocument);
  const deleteDocument = useMutation(api.knowledgeBase.deleteDocument);

  // Initialize state from document
  React.useEffect(() => {
    if (!document) return;
    setTitle(document.title ?? "");
    setContentMarkdown(sanitizeEmbeddedSourceMarkdown(document.contentMarkdown ?? ""));
    setStatus((document.status as DocumentStatus) ?? "draft");
    setFolderPath(document.folderPath ?? "");
    setTags(document.tags ?? []);
    setLastSaved(document.updatedAt);
  }, [document]);

  const hasUnsavedChanges = React.useMemo(() => {
    if (!document) return false;

    const currentTitle = title.trim();
    const originalTitle = (document.title ?? "").trim();
    if (currentTitle !== originalTitle) return true;

    const currentContent = sanitizeEmbeddedSourceMarkdown(contentMarkdown ?? "").trim();
    const originalContent = sanitizeEmbeddedSourceMarkdown(document.contentMarkdown ?? "").trim();
    if (currentContent !== originalContent) return true;

    const currentStatus = status;
    const originalStatus = (document.status as DocumentStatus) ?? "draft";
    if (currentStatus !== originalStatus) return true;

    const currentFolder = normalizeFolderPath(folderPath);
    const originalFolder = normalizeFolderPath(document.folderPath);
    if (currentFolder !== originalFolder) return true;

    const normalizeTagList = (list: string[] | undefined): string[] =>
      (list ?? [])
        .map((tag) => tag.trim())
        .filter(Boolean)
        .map((tag) => tag.toLowerCase())
        .sort();

    const currentTags = normalizeTagList(tags);
    const originalTags = normalizeTagList(document.tags ?? []);
    if (currentTags.length !== originalTags.length) return true;
    for (let i = 0; i < currentTags.length; i += 1) {
      if (currentTags[i] !== originalTags[i]) return true;
    }

    return false;
  }, [contentMarkdown, document, folderPath, status, tags, title]);

  const saveDocument = React.useCallback(async () => {
    if (!authUserId || !documentId || !title.trim() || !contentMarkdown.trim()) {
      return;
    }
    if (!hasUnsavedChanges) {
      return;
    }

    setIsSaving(true);
    try {
      const sanitizedContent = sanitizeEmbeddedSourceMarkdown(contentMarkdown).trim();
      await upsertDocument({
        authUserId,
        documentId: documentId as any,
        title: title.trim(),
        contentMarkdown: sanitizedContent,
        status,
        folderPath: normalizeFolderPath(folderPath) || undefined,
        tags: tags.length > 0 ? tags : undefined,
        category: document?.category ?? "general",
        slug: document?.slug ?? undefined,
        summary: document?.summary ?? undefined,
        sourcePath: sanitizeSourcePathDisplay(document?.sourcePath),
        sourceType: (document?.sourceType as SourceType) ?? "manual",
      });
      setLastSaved(Date.now());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save document");
    } finally {
      setIsSaving(false);
    }
  }, [authUserId, documentId, title, contentMarkdown, hasUnsavedChanges, status, folderPath, tags, document, upsertDocument]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+S or Ctrl+S to save
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault();
        saveDocument();
      }
      // Esc to close
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [saveDocument, onClose]);

  const onArchive = React.useCallback(async () => {
    if (!authUserId || !documentId || isArchiving || isDeleting) return;
    setIsArchiving(true);
    try {
      const result = await deleteDocument({
        authUserId,
        documentId: documentId as any,
      });
      if (!result?.success) {
        toast.error("Unable to archive document");
        return;
      }
      toast.success("Document archived");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to archive document");
    } finally {
      setIsArchiving(false);
    }
  }, [authUserId, documentId, deleteDocument, onClose, isArchiving, isDeleting]);

  const onHardDelete = React.useCallback(async () => {
    if (!authUserId || !documentId || isDeleting || isArchiving) return;
    setIsDeleting(true);
    try {
      const result = await deleteDocument({
        authUserId,
        documentId: documentId as any,
        hardDelete: true,
      });
      if (!result?.success) {
        toast.error("Unable to delete document");
        return;
      }
      toast.success("Document deleted");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete document");
    } finally {
      setIsDeleting(false);
    }
  }, [authUserId, documentId, deleteDocument, onClose, isDeleting, isArchiving]);

  const folderSegments = React.useMemo(() => {
    if (!folderPath) return [];
    return folderPath.split("/").filter(Boolean);
  }, [folderPath]);

  const sourceReference = React.useMemo(() => {
    const normalized = sanitizeSourcePathDisplay(document?.sourcePath);
    if (!normalized) return undefined;
    const candidate = normalizeSourceReference(normalized);
    return isHttpUrl(candidate) ? candidate : undefined;
  }, [document?.sourcePath]);

  if (!document) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading document...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Compact Header with Metadata */}
      <div className="flex flex-col gap-4 border-b pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              className="text-2xl font-bold border-0 px-0 focus-visible:ring-0"
            />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Home className="size-3" />
              {folderSegments.length === 0 ? (
                <span>Home</span>
              ) : (
                <>
                  <span>Home</span>
                  {folderSegments.map((segment, index) => (
                    <React.Fragment key={index}>
                      <ChevronRight className="size-3" />
                      <span>{segment}</span>
                    </React.Fragment>
                  ))}
                </>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={statusBadgeClass(status)}>
              {status}
            </Badge>
            {tags.length > 0 && (
              <div className="flex items-center gap-1">
                {tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {lastSaved && (
              <span className="text-xs text-muted-foreground">
                Last saved {formatDate(lastSaved, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            )}
            {sourceReference && (
              <a
                href={sourceReference}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
              >
                <ExternalLink className="size-3" />
                Source file
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as DocumentStatus)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={saveDocument}
              disabled={isSaving || isArchiving || isDeleting}
            >
              <Save className="mr-2 size-4" />
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button variant="outline" size="sm" disabled={isSaving || isArchiving || isDeleting} onClick={onArchive}>
              {isArchiving ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Archive className="mr-2 size-4" />}
              {isArchiving ? "Archiving..." : "Archive"}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="dangerOutline"
                  size="sm"
                  disabled={isSaving || isArchiving || isDeleting}
                >
                  {isDeleting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Trash2 className="mr-2 size-4" />}
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete document permanently?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This cannot be undone. The document will be removed from the database.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction variant="dangerOutline" disabled={isDeleting} onClick={onHardDelete}>
                    {isDeleting ? "Deleting..." : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Inline Markdown Editor */}
      <div data-color-mode={resolvedTheme === "dark" ? "dark" : "light"}>
        <MDEditor
          value={contentMarkdown}
          onChange={(value) => setContentMarkdown(value ?? "")}
          preview="live"
          previewOptions={markdownPreviewOptions}
          visibleDragbar={false}
          height={600}
          textareaProps={{
            placeholder: "# Start writing...",
            "aria-label": "Document markdown editor",
          }}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>
          Press <kbd className="px-1.5 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded border">Cmd+S</kbd> to save,{" "}
          <kbd className="px-1.5 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded border">Esc</kbd> to close
        </div>
        {isSaving && <span>Saving...</span>}
      </div>
    </div>
  );
}
